import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDown, ArrowRight, Check, Compass, Rocket, Users } from 'lucide-react';
import { SlashMark } from '../AmdenMark';
import { scrollToId } from '@/lib/scroll';

const FLOATING_ICONS = { compass: Compass, users: Users, rocket: Rocket };
const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="hero-mask-line">
    <motion.span
      className="block"
      initial={reduce ? false : { y: '110%' }}
      animate={reduce ? undefined : { y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const ServiceLineHero = ({ content, accent, accentDark, accentSoft, testidPrefix }) => {
  const reduce = useReducedMotion();
  const FloatingIcon = FLOATING_ICONS[content.floatingCard.icon] || Compass;

  return (
    <section
      aria-labelledby={`${testidPrefix}-heading`}
      data-testid={`${testidPrefix}-hero`}
      className="relative overflow-hidden bg-white"
    >
      <SlashMark
        className="pointer-events-none absolute -right-16 top-10 h-[380px] w-[380px] opacity-[0.05]"
        bar="#0B1729"
        dot={accent}
      />
      <div
        className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full opacity-50 blur-3xl"
        style={{ backgroundColor: accentSoft }}
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-20 pt-14 sm:px-6 sm:pt-20 lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8 lg:pb-24 lg:pt-20">
        <div className="lg:col-span-6">
          <motion.p
            data-testid={`${testidPrefix}-eyebrow`}
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em]"
            style={{ color: accent }}
          >
            <span className="h-px w-8" style={{ backgroundColor: accent }} aria-hidden="true" />
            {content.eyebrow}
          </motion.p>

          <h1
            id={`${testidPrefix}-heading`}
            data-testid={`${testidPrefix}-heading`}
            className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
          >
            {content.headingLines.map((line, i) => (
              <MaskedLine key={line} delay={0.1 + i * 0.12} reduce={reduce}>
                {i === content.accentLineIndex ? (
                  <span className="font-serif font-semibold italic" style={{ color: accent }}>
                    {line}
                  </span>
                ) : (
                  line
                )}
              </MaskedLine>
            ))}
          </h1>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
          >
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {content.body}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={content.primaryCta.href}
                data-testid={`${testidPrefix}-discuss-btn`}
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-4 text-base font-bold text-white transition-colors hover:bg-navy-700"
              >
                {content.primaryCta.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
              {content.secondaryCta && (
                <button
                  type="button"
                  onClick={() => scrollToId(content.secondaryCta.target)}
                  data-testid={`${testidPrefix}-explore-btn`}
                  className="group inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy-900 px-7 py-[14px] text-base font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
                >
                  {content.secondaryCta.label}
                  <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
                </button>
              )}
            </div>
            {content.credibility && (
              <p
                className="mt-6 flex items-center gap-2.5 text-sm font-semibold text-navy-800"
                data-testid={`${testidPrefix}-credibility`}
              >
                <Check className="h-4 w-4 shrink-0" style={{ color: accent }} aria-hidden="true" />
                {content.credibility}
              </p>
            )}
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={reduce ? undefined : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-xl border border-slate-200 shadow-lift">
              <img
                src={content.image?.src}
                alt={content.image.alt}
                width={content.image.width}
                height={content.image.height}
                fetchPriority="high"
                data-testid={`${testidPrefix}-hero-image`}
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <motion.div
              data-testid={`${testidPrefix}-hero-card`}
              initial={reduce ? false : { opacity: 0, y: 24 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="absolute -bottom-7 left-4 flex max-w-xs items-center gap-3.5 rounded-xl border border-slate-200 bg-white p-5 shadow-lift sm:left-8"
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: accentSoft, color: accentDark }}
              >
                <FloatingIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-[0.14em]" style={{ color: accentDark }}>
                  {content.floatingCard.label}
                </span>
                <span className="mt-0.5 block text-sm font-semibold leading-snug text-navy-900">
                  {content.floatingCard.text}
                </span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
